import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  ClockCounterClockwiseIcon,
  CopySimpleIcon,
  LightningIcon,
  MagnifyingGlassIcon,
  PauseIcon,
  PaperPlaneTiltIcon,
  PencilSimpleIcon,
  PlayIcon,
  PlusIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import type { Action } from '@/api/actions-service';
import { ActionsService } from '@/api/actions-service';
import {
  actionCatalogOptions,
  actionsOptions,
  isLiveRun,
  LIVE_RUN_POLL_MS,
} from '@/hooks/query-options';
import { PageContent } from '@/components/page-content';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmModal } from '@/components/ui/confirm-modal';
import { DataTable } from '@/components/ui/data-table';
import { Input } from '@/components/ui/input';
import {
  RowActionsMenu,
  type RowAction,
} from '@/components/ui/row-actions-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useProjects } from '@/hooks/use-projects';
import { useFeatures } from '@/hooks/use-features';
import { lockedChannelPlan, lockedIntegrationPlan } from '@/lib/plans';
import { ConfigureActionDialog } from '@/components/actions/configure-action-dialog';
import { WatchPromptDialog } from '@/components/actions/watch/watch-prompt';
import {
  automationSummary,
  FREQUENCY_LABELS,
  isArmed,
  isScheduled,
  triggerLabel,
} from '@/components/actions/run-mode-labels';
import { actionVisual, ACCENT_TILE } from '@/components/actions/action-visuals';
import {
  groupActionsByType,
  lastRunTime,
  nextRunTime,
  rowTitle,
  type ActionGroup,
  type ActionLeaf,
  type ActionRow,
} from '@/components/actions/action-groups';
import {
  RunActionFormDialog,
  RUN_FORM_ACTION_KEYS,
} from '@/components/actions/run-forms';
import { cn } from '@/utils/cn';

type ActionsSearch = { editAction?: 'account.watch' };

export const Route = createFileRoute(
  '/_auth/_side-layout/actions/your-actions/actions',
)({
  component: YourActionsPage,
  validateSearch: (search): ActionsSearch => ({
    editAction:
      search.editAction === 'account.watch'
        ? ('account.watch' as const)
        : undefined,
  }),
  loader: ({ context: { queryClient } }) =>
    Promise.all([
      queryClient.ensureQueryData(actionsOptions()),
      queryClient.ensureQueryData(actionCatalogOptions()),
    ]),
});

// Date and time wrap as two units, so a narrow column breaks between them
// instead of mid-date.
function RunDate({
  value,
  className,
}: {
  value: string | number;
  className?: string;
}) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return <span className={className}>—</span>;
  return (
    <span className={className}>
      <span className="whitespace-nowrap">{format(date, 'MMM d,')}</span>{' '}
      <span className="whitespace-nowrap">{format(date, 'h:mm a')}</span>
    </span>
  );
}

// A saved destination whose channel the org's plan no longer includes (a downgrade).
function lockedDestinationPlan(
  action: Action,
  isEnabled: (feature: string) => boolean,
): 'Essentials' | 'Pro' | null {
  const raw = (action.options as { destinations?: unknown } | null)
    ?.destinations;
  if (!Array.isArray(raw)) return null;
  for (const item of raw) {
    const dest = item as {
      type?: string;
      config?: { channel?: string; type?: string };
    };
    const channel =
      dest.type === 'webhook'
        ? 'webhook'
        : (dest.config?.channel ?? dest.config?.type);
    const plan = channel
      ? lockedChannelPlan(channel, isEnabled)
      : dest.type
        ? lockedIntegrationPlan(dest.type, isEnabled)
        : null;
    if (plan) return plan;
  }
  return null;
}

type StatusKind = 'setup' | 'locked' | 'paused' | 'idle' | 'active';

// One rule for an action's status, shared by its badge and its group's rollup:
// draft → configure; a plan-locked destination and paused (disabled) win over
// mode; manual actions idle between runs; scheduled/event actions are live.
function actionStatus(
  action: Action,
  isEnabled: (feature: string) => boolean,
): { kind: StatusKind; plan?: 'Essentials' | 'Pro' } {
  if (action.status !== 'ready') return { kind: 'setup' };
  const plan = lockedDestinationPlan(action, isEnabled);
  if (plan) return { kind: 'locked', plan };
  if (!action.enabled) return { kind: 'paused' };
  return { kind: isArmed(action) ? 'active' : 'idle' };
}

function StatusBadge({ action }: { action: Action }) {
  const { isEnabled } = useFeatures();
  const { kind, plan } = actionStatus(action, isEnabled);
  if (kind === 'setup') {
    return (
      <Badge size="sm" variant="warning">
        Configure to Run
      </Badge>
    );
  }
  if (kind === 'locked') {
    return (
      <Badge size="sm" variant="warning">
        Needs {plan} Plan
      </Badge>
    );
  }
  if (kind === 'active') {
    return (
      <Badge size="sm" variant="success">
        Active
      </Badge>
    );
  }
  return (
    <Badge size="sm" variant="neutral">
      {kind === 'paused' ? 'Paused' : 'Idle'}
    </Badge>
  );
}

const Dash = () => <span className="text-muted-foreground text-sm">—</span>;

function ActionTile({ actionKey }: { actionKey: string }) {
  const { Icon, accent } = actionVisual(actionKey);
  return (
    <span
      className={cn(
        'flex size-9 shrink-0 items-center justify-center rounded-md ring-1 ring-inset [&_svg]:size-4.5',
        ACCENT_TILE[accent],
      )}
    >
      <Icon />
    </span>
  );
}

// A type's rollup: what runs it, across its actions.
function GroupMode({ group }: { group: ActionGroup }) {
  const actions = group.children.map((child) => child.action);
  const scheduled = actions.filter(isScheduled).length;
  const triggered = actions.filter((action) => action.triggers.length).length;
  const parts = [
    scheduled > 0 && `${scheduled} Scheduled`,
    triggered > 0 && `${triggered} Triggered`,
  ].filter(Boolean);
  return (
    <span className="text-muted-foreground text-sm">
      {parts.length ? parts.join(' · ') : 'Manual'}
    </span>
  );
}

// Problems first, so a collapsed group can't hide a failure, a draft, or a
// plan-locked destination; otherwise how many are active or paused.
function GroupStatus({ group }: { group: ActionGroup }) {
  const { isEnabled } = useFeatures();
  const actions = group.children.map((child) => child.action);
  const statuses = actions.map((action) => actionStatus(action, isEnabled));
  const count = (kind: StatusKind) =>
    statuses.filter((status) => status.kind === kind).length;
  const needs = (total: number) => (total === 1 ? 'Needs' : 'Need');
  const failed = actions.filter((a) => a.last_run_status === 'failed').length;
  const running = actions.filter((a) => a.last_run_status === 'running').length;
  const setup = count('setup');
  const locked = count('locked');
  // Pro includes Essentials, so it's the one upgrade that unlocks them all.
  const plan = statuses.some((status) => status.plan === 'Pro')
    ? 'Pro'
    : 'Essentials';
  const active = count('active');
  const paused = count('paused');
  const problems = failed + running + setup + locked > 0;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {failed > 0 && (
        <Badge size="sm" variant="error">
          {failed} Failed
        </Badge>
      )}
      {running > 0 && (
        <Badge size="sm" variant="default">
          {running} Running
        </Badge>
      )}
      {setup > 0 && (
        <Badge size="sm" variant="warning">
          {setup} {needs(setup)} Setup
        </Badge>
      )}
      {locked > 0 && (
        <Badge size="sm" variant="warning">
          {locked} {needs(locked)} {plan} Plan
        </Badge>
      )}
      {!problems && active > 0 && (
        <Badge size="sm" variant="success">
          {active} Active
        </Badge>
      )}
      {!problems && paused > 0 && (
        <Badge size="sm" variant="neutral">
          {paused} Paused
        </Badge>
      )}
      {!problems && !active && !paused && (
        <Badge size="sm" variant="neutral">
          Idle
        </Badge>
      )}
    </div>
  );
}

const LAST_RUN_DOT: Record<string, string> = {
  succeeded: 'bg-success',
  failed: 'bg-destructive',
  running: 'bg-primary animate-pulse',
};

function buildColumns(handlers: {
  onEdit: (action: Action) => void;
  onRun: (action: Action) => void;
  onRunForm: (action: Action) => void;
  onSendTest: (action: Action) => void;
  onTogglePause: (action: Action) => void;
  onDuplicate: (action: Action) => void;
  onDelete: (action: Action) => void;
  viewActivity: () => void;
  runningId: string | null;
  testingId: string | null;
  projectNames: Record<string, string>;
}): ColumnDef<ActionRow>[] {
  return [
    {
      id: 'action',
      accessorFn: (row) => rowTitle(row).toLowerCase(),
      header: 'Action',
      cell: ({ row }) => {
        const original = row.original;
        if (original.kind === 'group') {
          const title = rowTitle(original);
          return (
            <div className="flex min-w-0 items-center gap-3">
              <ActionTile actionKey={original.actionKey} />
              <div className="min-w-0">
                <div className="max-w-40 truncate font-medium" title={title}>
                  {title}
                </div>
                <div className="text-muted-foreground text-xs">
                  {original.children.length} actions
                </div>
              </div>
            </div>
          );
        }
        const { action, entry } = original;
        const agents = entry?.agents ?? [];
        return (
          <div
            className={cn(
              'flex min-w-0 items-center gap-3',
              row.depth > 0 && 'pl-5',
            )}
          >
            <ActionTile actionKey={action.action_key} />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span
                  className="max-w-40 truncate font-medium"
                  title={action.name}
                >
                  {action.name}
                </span>
                {entry?.kind === 'ai' && (
                  <Badge size="sm" variant="default">
                    AI
                  </Badge>
                )}
              </div>
              {/* Named agents (they work together — never a sequence); the
                  catalog subtitle balances agentless rows. */}
              {(agents.length > 0 || entry?.subtitle) && (
                <div className="text-muted-foreground max-w-40 truncate text-xs">
                  {agents.length > 0
                    ? agents.map((agent) => agent.label).join(' · ')
                    : entry?.subtitle}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      id: 'mode',
      header: 'Mode',
      cell: ({ row }) => {
        const original = row.original;
        if (original.kind === 'group') return <GroupMode group={original} />;
        const { action } = original;
        const { label, Icon: ModeIcon } = automationSummary(action);
        const detail = [
          ...(action.schedule
            ? [FREQUENCY_LABELS[action.schedule.frequency]]
            : []),
          ...action.triggers.map(triggerLabel),
        ]
          .filter(Boolean)
          .join(', ');
        return (
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-sm">
              <ModeIcon className="text-muted-foreground size-4 shrink-0" />
              {label}
            </div>
            {detail && (
              <div
                className="text-muted-foreground max-w-36 truncate text-xs"
                title={detail}
              >
                {detail}
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: 'scope',
      header: 'Scope',
      cell: ({ row }) => {
        const original = row.original;
        if (original.kind === 'group') {
          return original.children.every(
            (child) => child.action.scope === 'org',
          ) ? (
            <span className="text-sm">Organization</span>
          ) : (
            <Dash />
          );
        }
        const { action } = original;
        if (action.scope === 'org') {
          return <span className="text-sm">Organization</span>;
        }
        const names = action.project_ids.map(
          (id) => handlers.projectNames[id] ?? 'Unknown project',
        );
        return (
          <div className="min-w-0">
            <div className="truncate text-sm">{names[0]}</div>
            {names.length > 1 && (
              <div className="text-muted-foreground text-xs">
                +{names.length - 1} more
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: 'last_run',
      accessorFn: lastRunTime,
      sortUndefined: 'last',
      header: 'Last Run',
      cell: ({ row }) => {
        const original = row.original;
        if (original.kind === 'group') {
          const last = lastRunTime(original);
          return last === undefined ? (
            <Dash />
          ) : (
            <RunDate value={last} className="text-muted-foreground text-sm" />
          );
        }
        const { action } = original;
        if (!action.last_run_at) return <Dash />;
        const failure =
          action.last_run_status === 'failed' ? action.last_run_error : null;
        return (
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm">
              <span
                className={cn(
                  'size-1.5 shrink-0 rounded-full',
                  LAST_RUN_DOT[action.last_run_status ?? ''] ??
                    'bg-muted-foreground',
                )}
              />
              <RunDate value={action.last_run_at} />
            </div>
            {failure && (
              <div
                className="text-muted-foreground max-w-36 truncate text-xs"
                title={failure}
              >
                {failure}
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: 'next_run',
      accessorFn: nextRunTime,
      sortUndefined: 'last',
      header: 'Next Run',
      cell: ({ row }) => {
        const original = row.original;
        if (original.kind === 'group') {
          const next = nextRunTime(original);
          return next === undefined ? (
            <Dash />
          ) : (
            <RunDate value={next} className="text-sm" />
          );
        }
        const { action } = original;
        if (action.triggers.length > 0 && !isScheduled(action)) {
          if (!action.enabled) return <Dash />;
          return (
            <span className="text-muted-foreground text-sm whitespace-nowrap">
              On Trigger
            </span>
          );
        }
        if (!action.next_run_at || !action.enabled) return <Dash />;
        return <RunDate value={action.next_run_at} className="text-sm" />;
      },
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const original = row.original;
        return original.kind === 'group' ? (
          <GroupStatus group={original} />
        ) : (
          <StatusBadge action={original.action} />
        );
      },
    },
    {
      id: 'manage',
      header: () => <span className="sr-only">Manage</span>,
      cell: ({ row }) => {
        const original = row.original;
        // A group is a rollup, not an action: it only expands.
        if (original.kind === 'group') return null;
        const { action, entry } = original;
        const isReady = action.status === 'ready';
        const runnable = isReady && entry?.manual_run === 'config';
        // Form-driven actions (a prospect, a lead) run from their own dialog.
        const formRunnable = isReady && entry?.manual_run === 'form';
        const menu: RowAction[] = [
          {
            label: 'Edit',
            icon: <PencilSimpleIcon />,
            onSelect: () => handlers.onEdit(action),
          },
          ...(runnable || formRunnable
            ? [
                {
                  label: 'Run',
                  icon: <PlayIcon />,
                  onSelect: () =>
                    formRunnable
                      ? handlers.onRunForm(action)
                      : handlers.onRun(action),
                },
              ]
            : []),
          ...(isReady &&
          entry?.manual_run === 'none' &&
          entry.run_modes.includes('trigger')
            ? [
                {
                  label: 'Send Test',
                  icon: <PaperPlaneTiltIcon />,
                  disabled: handlers.testingId === action.id,
                  onSelect: () => handlers.onSendTest(action),
                },
              ]
            : []),
          {
            label: 'View Activity',
            icon: <ClockCounterClockwiseIcon />,
            onSelect: () => handlers.viewActivity(),
          },
          ...(isReady && isArmed(action)
            ? [
                {
                  label: action.enabled ? 'Pause' : 'Resume',
                  icon: action.enabled ? <PauseIcon /> : <PlayIcon />,
                  onSelect: () => handlers.onTogglePause(action),
                },
              ]
            : []),
          {
            label: 'Duplicate',
            icon: <CopySimpleIcon />,
            onSelect: () => handlers.onDuplicate(action),
          },
          {
            label: 'Remove from Library',
            icon: <TrashIcon />,
            destructive: true,
            onSelect: () => handlers.onDelete(action),
          },
        ];
        return (
          <div className="flex items-center justify-end gap-1">
            {(runnable || formRunnable) && (
              <Button
                variant="outline"
                size="sm"
                iconLeft={<PlayIcon />}
                loading={handlers.runningId === action.id}
                onClick={() =>
                  formRunnable
                    ? handlers.onRunForm(action)
                    : handlers.onRun(action)
                }
              >
                Run
              </Button>
            )}
            <RowActionsMenu actions={menu} label={`Manage ${action.name}`} />
          </div>
        );
      },
    },
  ];
}

function ActionsTable() {
  const { editAction } = Route.useSearch();
  const { userRole } = Route.useRouteContext();
  const {
    data: configured = [],
    isLoading,
    isError: actionsError,
  } = useQuery({
    ...actionsOptions(),
    refetchInterval: (query) =>
      (query.state.data ?? []).some((action) =>
        isLiveRun(action.last_run_status, action.last_run_at),
      )
        ? LIVE_RUN_POLL_MS
        : false,
  });
  const { data: catalog } = useQuery(actionCatalogOptions());
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { projects } = useProjects();
  const { isEnabled } = useFeatures();
  const projectNames = useMemo(
    () =>
      Object.fromEntries(
        (projects ?? []).map((p) => [p.projectId, p.name]),
      ) as Record<string, string>,
    [projects],
  );

  const [editing, setEditing] = useState<Action | null>(null);
  const linkedAction = editAction
    ? (configured.find((action) => action.action_key === editAction) ?? null)
    : null;
  const activeEditor = editing ?? linkedAction;
  const canSetUpWatch =
    isEnabled('targetAccounts') &&
    (userRole === 'owner' || userRole === 'admin');
  const [runFormFor, setRunFormFor] = useState<Action | null>(null);
  const [deleting, setDeleting] = useState<Action | null>(null);
  const [search, setSearch] = useState('');
  const [modeFilter, setModeFilter] = useState<
    'all' | 'once' | 'schedule' | 'trigger'
  >('all');

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['actions'] });

  const entryFor = useMemo(() => {
    const map = new Map(
      (catalog?.actions ?? []).map((entry) => [entry.key, entry] as const),
    );
    return (key: string) => map.get(key);
  }, [catalog]);

  const run = useMutation({
    mutationFn: (action: Action) => ActionsService.run(action.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['action-runs'] });
      await invalidate();
      toast.success('Action started');
    },
    onError: () => toast.error('Could not run the action. Try again.'),
  });

  const sendTest = useMutation({
    mutationFn: (action: Action) => ActionsService.sendTest(action.id),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['action-runs'] });
      await invalidate();
      if (result.failures.length === 0) toast.success('Test sent');
      else
        toast.error("Test didn't reach the destination", {
          description: `${result.failures[0]?.replace(/\.$/, '')}. Check the destination settings and try again.`,
        });
    },
    onError: (err) =>
      toast.error(
        err instanceof Error && err.message
          ? err.message
          : 'Could not send the test. Try again.',
      ),
  });

  const togglePause = useMutation({
    mutationFn: (action: Action) =>
      ActionsService.update(action.id, {
        enabled: !action.enabled,
      }),
    onSuccess: (_data, action) => {
      toast.success(
        action.enabled ? `${action.name} paused` : `${action.name} resumed`,
      );
      invalidate();
    },
    onError: () => toast.error('Could not update the action'),
  });

  const duplicate = useMutation({
    mutationFn: (action: Action) =>
      ActionsService.create({
        name: `${action.name} Copy`,
        action_key: action.action_key,
        status: action.status,
        scope: action.scope,
        project_ids: action.project_ids,
        profile_id: action.profile_id,
        agent_overrides: action.agent_overrides,
        options: action.options,
        memory_override: action.memory_override,
        schedule: action.schedule,
        triggers: action.triggers,
        cooldown: action.cooldown,
        enabled: action.enabled,
      }),
    onSuccess: (_data, action) => {
      toast.success(`Duplicated ${action.name}`);
      invalidate();
    },
    onError: () => toast.error('Could not duplicate the action'),
  });

  const remove = useMutation({
    mutationFn: (action: Action) => ActionsService.remove(action.id),
    onSuccess: (_data, action) => {
      toast.success(`${action.name} removed`);
      setDeleting(null);
      invalidate();
    },
    onError: () => toast.error('Could not remove the action'),
  });

  const term = search.trim().toLowerCase();
  const filtering = term !== '' || modeFilter !== 'all';
  const rows = useMemo<ActionRow[]>(() => {
    const leaves = configured
      .filter((action) => {
        if (
          (modeFilter === 'once' && isArmed(action)) ||
          (modeFilter === 'schedule' && !isScheduled(action)) ||
          (modeFilter === 'trigger' && action.triggers.length === 0)
        ) {
          return false;
        }
        if (!term) return true;
        const entry = entryFor(action.action_key);
        return (
          action.name.toLowerCase().includes(term) ||
          (entry?.label.toLowerCase().includes(term) ?? false) ||
          (entry?.agents ?? []).some((agent) =>
            agent.label.toLowerCase().includes(term),
          )
        );
      })
      .map(
        (action): ActionLeaf => ({
          kind: 'action',
          action,
          entry: entryFor(action.action_key),
        }),
      );
    return groupActionsByType(leaves);
  }, [configured, term, modeFilter, entryFor]);

  const columns = useMemo(
    () =>
      buildColumns({
        onEdit: setEditing,
        onRun: (action) => run.mutate(action),
        onRunForm: setRunFormFor,
        onSendTest: (action) => sendTest.mutate(action),
        onTogglePause: (action) => togglePause.mutate(action),
        onDuplicate: (action) => duplicate.mutate(action),
        onDelete: setDeleting,
        viewActivity: () => navigate({ to: '/actions/your-actions/activity' }),
        runningId: run.isPending ? (run.variables?.id ?? null) : null,
        testingId: sendTest.isPending ? (sendTest.variables?.id ?? null) : null,
        projectNames,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      run.isPending,
      run.variables,
      sendTest.isPending,
      sendTest.variables,
      projectNames,
    ],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search your actions…"
          className="sm:max-w-xs"
        />
        <Select
          value={modeFilter}
          onValueChange={(v) => setModeFilter(v as typeof modeFilter)}
        >
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Modes</SelectItem>
            <SelectItem value="once">Manual</SelectItem>
            <SelectItem value="schedule">Scheduled</SelectItem>
            <SelectItem value="trigger">Triggered</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        pageSize={25}
        enableSorting
        isLoading={isLoading}
        getRowId={(row) =>
          row.kind === 'group' ? `group:${row.actionKey}` : row.action.id
        }
        getSubRows={(row) => (row.kind === 'group' ? row.children : undefined)}
        // Clicking an action opens Edit; clicking a group opens it (clicks on
        // the Run button / row menu are ignored by the table's guard).
        onRowClick={(row) => {
          if (row.kind === 'action') setEditing(row.action);
        }}
        emptyMessage={filtering ? 'No Matching Actions' : 'No Actions Yet'}
        emptyDescription={
          filtering
            ? 'Try a different search or mode.'
            : 'Browse the catalog and add an action to run once or on a schedule.'
        }
        emptyIcon={filtering ? MagnifyingGlassIcon : LightningIcon}
        emptyAction={
          filtering ? undefined : (
            <Button asChild iconLeft={<PlusIcon />}>
              <Link to="/actions/catalog">Browse Catalog</Link>
            </Button>
          )
        }
      />

      {activeEditor && (
        <ConfigureActionDialog
          open
          onOpenChange={(open) => {
            if (open) return;
            setEditing(null);
            if (editAction)
              void navigate({
                to: Route.fullPath,
                search: { editAction: undefined },
              });
          }}
          actionKey={activeEditor.action_key}
          action={activeEditor}
          onRunForm={() => setRunFormFor(activeEditor)}
        />
      )}

      {editAction === 'account.watch' &&
        !isLoading &&
        !actionsError &&
        !linkedAction &&
        canSetUpWatch && (
          <WatchPromptDialog
            open
            onOpenChange={(open) => {
              if (!open)
                void navigate({
                  to: Route.fullPath,
                  search: { editAction: undefined },
                });
            }}
          />
        )}

      {runFormFor &&
        (RUN_FORM_ACTION_KEYS as readonly string[]).includes(
          runFormFor.action_key,
        ) && (
          <RunActionFormDialog
            actionKey={
              runFormFor.action_key as (typeof RUN_FORM_ACTION_KEYS)[number]
            }
            actionName={runFormFor.name}
            open
            onOpenChange={(open) => !open && setRunFormFor(null)}
          />
        )}

      <ConfirmModal
        open={deleting !== null}
        title="Remove Action"
        message={`Remove "${deleting?.name}" from your library? This can't be undone.`}
        variant="destructive"
        confirmText="Remove"
        loading={remove.isPending}
        onFinish={async () => {
          if (deleting) await remove.mutateAsync(deleting);
        }}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}

function YourActionsPage() {
  return (
    <PageContent>
      <ActionsTable />
    </PageContent>
  );
}
